import { useRef, useState } from "react";
import { ReactCropperElement } from "react-cropper";
import { ImageUploadInputPropsType } from "./ImageUploadInput.props";

export function useCropper(props: ImageUploadInputPropsType) {
  const { maxWidth, maxHeight, updateImage, mimeType, minWidth, minHeight } =
    props;

  const [isCropping, setIsCropping] = useState<boolean>(false);
  const cropperRef = useRef<ReactCropperElement>();

  // Calculate output dimensions that maintain aspect ratio within min/max bounds
  const getOutputDimensions = (croppedWidth: number, croppedHeight: number) => {
    let width = croppedWidth;
    let height = croppedHeight;

    // If too small, scale up to meet minimum (maintaining aspect ratio)
    if (width < minWidth || height < minHeight) {
      const scaleUpRatio = Math.max(minWidth / width, minHeight / height);
      width = Math.round(width * scaleUpRatio);
      height = Math.round(height * scaleUpRatio);
    }

    // If too large, scale down to meet maximum (maintaining aspect ratio)
    if (width > maxWidth || height > maxHeight) {
      const scaleDownRatio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.round(width * scaleDownRatio);
      height = Math.round(height * scaleDownRatio);
    }

    return { width, height };
  };

  const getCropData = () => {
    if (typeof cropperRef.current?.cropper !== "undefined") {
      const data = cropperRef.current.cropper.getData();
      const { width, height } = getOutputDimensions(data.width, data.height);

      updateImage(
        cropperRef.current.cropper
          .getCroppedCanvas({
            width,
            height,
          })
          // set mime type to original mime type on image
          // prevents the file size from exploding when converting to base64
          .toDataURL(mimeType)
      );
    }
  };

  const updateIsCropping = (input: boolean) => {
    setIsCropping(input);
  };

  const finishCropping = () => {
    getCropData();
    updateIsCropping(false);
  };

  return {
    updateIsCropping,
    finishCropping,
    isCropping,
    cropperRef,
  };
}
